import { useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { colors } from '@tabbio/tokens';

interface CvListToolbarProps {
  addLabel: string;
  onAdd: () => void;
  showReorderHint: boolean;
}

/** Sticky list actions shared by onboarding, Main CV, and tailored CVs. */
export function CvListToolbar({
  addLabel,
  onAdd,
  showReorderHint,
}: CvListToolbarProps) {
  const { t } = useTranslation('seeker');
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pressed, setPressed] = useState(false);

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        paddingBlock: 4,
        backgroundColor: 'var(--bg)',
      }}
    >
      <button
        type="button"
        onClick={onAdd}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => {
          setHovered(false);
          setPressed(false);
        }}
        onMouseDown={() => setPressed(true)}
        onMouseUp={() => setPressed(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          setPressed(false);
        }}
        style={{
          display: 'flex',
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          minHeight: 56,
          paddingInline: 16,
          border: `1.5px dashed ${colors.primary}`,
          borderRadius: 16,
          backgroundColor: hovered
            ? 'rgba(94, 41, 255, 0.22)'
            : 'rgba(94, 41, 255, 0.12)',
          color: colors.primary,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontSize: 17,
          fontWeight: 600,
          letterSpacing: -0.17,
          outline: focused ? '2px solid var(--tabbio-focus-ring)' : 'none',
          outlineOffset: 2,
          transform: pressed ? 'scale(0.99)' : 'scale(1)',
          transition: 'background-color 150ms, transform 150ms, outline-color 150ms',
        }}
      >
        <Plus size={20} strokeWidth={2.2} />
        {addLabel}
      </button>

      {showReorderHint ? (
        <>
          <div
            style={{
              height: 1,
              marginTop: 24,
              backgroundColor: 'var(--tabbio-border-subtle)',
            }}
          />
          <span
            style={{
              display: 'block',
              marginTop: 24,
              color: 'var(--text-tertiary)',
              fontSize: 13,
              lineHeight: '18px',
              textAlign: 'center',
            }}
          >
            {t('cv.edit.dragToReorder')}
          </span>
        </>
      ) : null}
    </div>
  );
}
